import { Mountain, Waves, Tent, Factory, GraduationCap, Building2, PartyPopper, Globe2, ArrowRight, ChevronRight } from "lucide-react";
import { DESTINATIONS, CIRCUITS } from "@/data/siteData";
import { openEnquiry } from "@/lib/api";

export function Destinations() {
    return (
        <section id="destinations" className="py-20 border-b border-stone-300/70 overflow-hidden" data-testid="destinations-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <p className="font-script text-3xl sm:text-4xl text-saffron -rotate-1 inline-block">kahan chale?</p>
                <h2 className="mt-1 font-display font-black text-3xl sm:text-4xl text-navy tracking-tight">Where Do You Want To Go?</h2>
                <p className="mt-2 text-sm text-stone-500 uppercase tracking-wider font-bold">Battle-tested circuits across India — and beyond</p>

                <div className="mt-6 flex flex-wrap gap-2" data-testid="circuit-chips">
                    {CIRCUITS.map((c) => (
                        <span key={c} className="text-[11px] font-bold uppercase tracking-wider text-navy bg-cream-dark border border-stone-300 rounded-full px-3 py-1.5">{c}</span>
                    ))}
                </div>
            </div>

            <div className="mt-10 max-w-7xl mx-auto pl-4 sm:pl-6 lg:pl-8">
                <div className="flex gap-5 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-4 pr-4" data-testid="destinations-scroller">
                    {DESTINATIONS.map((d) => (
                        <article key={d.name} className="snap-start shrink-0 w-72 bg-white rounded-2xl border border-stone-300 overflow-hidden group hover:-translate-y-1 transition-transform" data-testid={`destination-card-${d.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                            <div className="relative h-44 overflow-hidden">
                                <img src={d.image} alt={`${d.name} group trip`} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                <span className="absolute bottom-3 left-3 bg-navy/90 text-white text-[10px] font-bold px-2 py-1 rounded">{d.days}</span>
                            </div>
                            <div className="p-5">
                                <div className="text-[10px] font-black text-saffron uppercase tracking-widest">{d.tag}</div>
                                <h3 className="font-display font-black text-xl text-navy mt-0.5">{d.name}</h3>
                                <p className="mt-2 text-xs text-stone-600 leading-relaxed">{d.desc}</p>
                                <div className="mt-4 pt-3 border-t border-stone-200 flex justify-between items-center">
                                    <span className="text-xs font-bold text-stone-500">Custom Packages</span>
                                    <button onClick={() => openEnquiry()} data-testid={`destination-explore-${d.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="inline-flex items-center gap-1 text-xs font-display font-black uppercase tracking-wider text-navy hover:text-saffron transition-colors">
                                        Explore <ChevronRight className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                        </article>
                    ))}
                    <div className="snap-start shrink-0 w-72 rounded-2xl bg-navy text-white p-8 flex flex-col justify-between" data-testid="custom-trip-card">
                        <div>
                            <p className="font-script text-3xl text-gold">kahin aur?</p>
                            <h3 className="font-display font-black text-2xl mt-1">Can't Find Your Destination?</h3>
                            <p className="mt-3 text-sm text-white/70 leading-relaxed">If people can go there in a group, we can plan it. India or international.</p>
                        </div>
                        <button onClick={() => openEnquiry()} data-testid="custom-trip-btn" className="mt-8 inline-flex items-center gap-2 self-start px-6 py-3 rounded-full bg-saffron hover:bg-saffron-dark text-xs font-display font-black uppercase tracking-wider transition-colors">
                            Build a Custom Trip <ArrowRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}

const ADVENTURES = [
    { title: "Adventure Trips", icon: Mountain, desc: "Treks, rafting, paragliding & snow camps" },
    { title: "Beach Escapes", icon: Waves, desc: "Goa, Andaman & coastal getaways" },
    { title: "Camping & Outdoor", icon: Tent, desc: "Riverside camps, bonfires & stargazing" },
    { title: "Industrial Visits", icon: Factory, desc: "Factory floors & live industry learning" },
    { title: "Educational Tours", icon: GraduationCap, desc: "Museums, science centres & heritage" },
    { title: "Corporate Offsites", icon: Building2, desc: "Retreats, MICE & team building" },
    { title: "Group Getaways", icon: PartyPopper, desc: "Friends, families & community trips" },
    { title: "International Trips", icon: Globe2, desc: "Group tours beyond borders" },
];

export function Adventures() {
    return (
        <section id="adventures" className="py-20 bg-navy-deep text-white" data-testid="adventures-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <p className="font-script text-3xl sm:text-4xl text-gold -rotate-1 inline-block">every mood covered</p>
                <h2 className="mt-1 font-display font-black text-3xl sm:text-4xl tracking-tight">Choose Your Kind of Adventure.</h2>

                <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                    {ADVENTURES.map((a) => (
                        <button
                            key={a.title}
                            onClick={() => openEnquiry()}
                            data-testid={`adventure-card-${a.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className="group text-left rounded-2xl border border-white/10 bg-white/5 p-6 hover:bg-saffron hover:border-saffron transition-colors"
                        >
                            <a.icon className="h-7 w-7 text-gold group-hover:text-white transition-colors" />
                            <h3 className="mt-4 font-display font-black text-base sm:text-lg">{a.title}</h3>
                            <p className="mt-1 text-xs text-white/60 group-hover:text-white/90">{a.desc}</p>
                        </button>
                    ))}
                </div>
            </div>
        </section>
    );
}
